import React from "react";
import {
  useInfiniteQuery,
  QueryClient,
  dehydrate,
} from "@tanstack/react-query";
import { useRouter } from "next/router";
import { twMerge } from "tailwind-merge";
import {
  Button,
  Columns,
  TextField,
  Pressable,
  Popover,
  Image,
  Slider,
  Toast,
} from "@gonoodle/gn-universe-ui";
import { Checkbox, CheckboxGroup } from "@gonoodle/gn-universe-ui/lib/next";
import {
  ClockIcon,
  SearchIcon,
  FilterIcon,
  StarIcon,
} from "@heroicons/react/outline";
import NextImage from "next/image";
import { isEmpty, filter, omit } from "lodash";
import { PAGES } from "@gonoodle/gn-universe-analytics-schema/src/constants";

import { useSearchFiltersQuery } from "../hooks";
import Layout from "../components/Layout";
import VideoTile from "../components/VideoTile";
import BlogCardTile from "../components/BlogCardTile";
import PrintableTile from "../components/PrintableTile";
import TextTile from "../components/TextTile";
import { withAuthorization } from "../containers";
import api from "../api";
import {
  QUERY_KEYS,
  ROUTE_PATHS,
  ROUTE_PERMISSIONS,
  CONTENT_TYPES,
  SEARCH_FILTER_TYPES,
  URLS,
} from "../constants";

const JSURL = require("jsurl");

const client = api();
const KEYWORD_SEARCH_TYPE = "keyword";
const FILTERS_SEARCH_TYPE = "filters";

const constructSearchUrl = (newQuery) => ({
  pathname: ROUTE_PATHS.SEARCH,
  query: newQuery,
});

const useSearchFilterQuery = (filterName) => {
  const {
    searchFilters,
    isSearchFiltersLoading,
    searchFiltersError,
  } = useSearchFiltersQuery();

  return {
    searchFilterList: searchFilters ? searchFilters[filterName] : undefined,
    isSearchFilterLoading: isSearchFiltersLoading,
    searchFilterError: searchFiltersError,
  };
};

/*
  Analyzes the current URL to retrieve the search filters applied by the user and
  activeSearchFilters: returns them as an object with the format {filterName1: [value1, value2...], ...}
  setActiveSearchFilters: updates the URL by adding/removing values
*/
function useActiveSearchFilters() {
  const router = useRouter();
  const activeSearchFilters = router.query?.filters
    ? JSURL.parse(router.query.filters)
    : {};
  const queryPrefix = "filters";

  const removeFiltersQueryParam = React.useCallback(() => {
    const query = { ...router.query };
    delete query[queryPrefix];

    return query;
  }, [router.query]);

  const addFiltersQueryParam = React.useCallback(
    (values) => {
      const query = { ...router.query };

      query[queryPrefix] = `${JSURL.stringify(values)}`;

      return query;
    },
    [router.query],
  );

  const commitFiltersToUrl = React.useCallback(
    (filters) => {
      const newQuery = isEmpty(filters)
        ? removeFiltersQueryParam()
        : addFiltersQueryParam(filters);

      router.replace(constructSearchUrl(newQuery), undefined, {
        shallow: true,
      });
    },
    [addFiltersQueryParam, removeFiltersQueryParam, router],
  );

  const setFilter = React.useCallback(
    ({ filterType, values }) => {
      const filters = router.query?.filters;
      const currentFilters = filters ? JSURL.parse(filters) : {};
      const updatedFilters = { ...currentFilters, [filterType]: values };

      commitFiltersToUrl(updatedFilters);
    },
    [router.query, commitFiltersToUrl],
  );

  const setFilters = React.useCallback(
    ({ filterType, values }) => {
      const filters = router.query?.filters;
      const currentFilters = filters ? JSURL.parse(filters) : {};
      let updatedFilters = { ...currentFilters };

      filterType.forEach((type) => {
        updatedFilters = { ...updatedFilters, [type]: values[type] };
      });

      commitFiltersToUrl(updatedFilters);
    },
    [router.query, commitFiltersToUrl],
  );

  const removeFilter = React.useCallback(
    (filterType) => {
      const currentFilters = JSURL.parse(router.query.filters);
      let updatedFilters = omit(currentFilters, filterType);
      Object.keys(updatedFilters).forEach((key) => {
        if (isEmpty(updatedFilters[key])) {
          updatedFilters = omit(updatedFilters, key);
        }
      });

      commitFiltersToUrl(updatedFilters);
    },
    [router.query?.filters, commitFiltersToUrl],
  );

  const clearFilters = React.useCallback(() => {
    commitFiltersToUrl(undefined);
  }, [commitFiltersToUrl]);

  return {
    activeSearchFilters,
    setActiveSearchFilters: ({ filterType, filterValues }) => {
      if (isEmpty(filterType) && isEmpty(filterValues)) {
        clearFilters();
      } else if (isEmpty(filterValues)) {
        removeFilter(filterType);
      } else if (Array.isArray(filterType)) {
        setFilters({ filterType, values: filterValues });
      } else {
        setFilter({ filterType, values: filterValues });
      }
    },
  };
}

/*
 activeSearchFilter: Gets the applied values in array format for a specific filter.
 setActiveSearchFilter: receives an array and sets the values for a specific filter
*/
function useActiveSearchFilter(filterType) {
  const {
    activeSearchFilters,
    setActiveSearchFilters,
  } = useActiveSearchFilters();

  if (filterType) {
    return {
      activeSearchFilter: activeSearchFilters[filterType],
      setActiveSearchFilter: (filterValues) =>
        setActiveSearchFilters({ filterType, filterValues }),
    };
  }

  return undefined;
}

const KeywordsFilter = ({ value, onSubmit }) => {
  const [searchTerm, setSearchTerm] = React.useState(value);

  React.useEffect(() => {
    setSearchTerm(value);
  }, [value]);

  return (
    <div className="flex items-center self-start w-full max-w-[500px] overflow-hidden transition-all h-12 border-b-[0.1px] border-gray">
      <SearchIcon className="mx-3 my-2 w-5 h-5 flex-shrink-0" />
      <div className="flex w-full [&>*]:w-full">
        <TextField
          aria-label="Search input"
          id="search"
          className="bg-transparent border-none shadow-none !text-lg text-white mb-1 focus:ring-0"
          type="text"
          onKeyUp={(e) => {
            if (e.key === "Enter") {
              onSubmit(searchTerm);
            }
          }}
          name="search"
          placeholder="E.g. Printables"
          value={searchTerm}
          onChange={setSearchTerm}
        />
      </div>

      {searchTerm && (
        <Pressable
          className="min-w-fit mx-3 my-2"
          onPress={() => {
            setSearchTerm("");
            onSubmit();
          }}
        >
          Clear text
        </Pressable>
      )}
    </div>
  );
};

function DurationFilter({
  isOpen = false,
  disabled,
  onOpen,
  onClose,
  handleSubmit,
}) {
  const alert = Toast.useAlert();
  const {
    searchFilterList: searchFilterValue,
    isSearchFilterLoading,
    searchFilterError,
  } = useSearchFilterQuery(SEARCH_FILTER_TYPES.DURATION);
  const min = parseInt(searchFilterValue?.[0], 10);
  const max = parseInt(searchFilterValue?.[searchFilterValue.length - 1], 10);
  const thumbNamePrefix = "duration-range-thumb-";

  const { activeSearchFilter: initialValues } = useActiveSearchFilter(
    SEARCH_FILTER_TYPES.DURATION,
  );

  const defaultValue = React.useMemo(
    () => (Array.isArray(initialValues) ? [...initialValues] : [min, max]),
    [initialValues, max, min],
  );

  if (searchFilterError) {
    alert.error("Something went wrong. Please reload page.");
    return null;
  }

  return (
    <Popover.Root
      offset={5}
      placement="bottom start"
      shouldFlip={false}
      shouldTriggerOnHover={true}
      restoreFocus={false}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
    >
      <Popover.Trigger
        ariaLabel={`${isOpen ? "Close" : "Open"} Duration Filter`}
        disabled={disabled}
        className={twMerge(
          "inline-flex items-center justify-center rounded border border-gray-500 hover:border-mint focus:border-mint transition-colors text-gray-300 text-sm hover:text-mint py-2 px-4 stroke-[#D4D4D8] hover:stroke-mint min-w-fit font-bold capitalize touch-pan-x focus:ring focus:outline-none focus:ring-mint",
          initialValues && "border-purple",
          disabled && "opacity-50 cursor-not-allowed",
        )}
      >
        <ClockIcon className="w-6 h-6 mr-3" />
        duration
      </Popover.Trigger>

      <Popover.Content className="outline-none overflow-hidden w-full md:w-screen-xs bg-gray-800 border border-purple rounded">
        {isOpen && (
          <form
            className="flex flex-col items-center"
            onSubmit={(event) => {
              event.preventDefault();

              const rangeValue = filter(event.target.elements, (element) =>
                element.name.includes(thumbNamePrefix),
              );
              const filterDurationValues = Array.from(rangeValue).map((input) =>
                Number(input.value),
              );

              handleSubmit({
                filterValues: filterDurationValues,
                filterType: SEARCH_FILTER_TYPES.DURATION,
              });
            }}
          >
            <Slider.Root
              defaultValue={defaultValue}
              className="flex flex-col mt-4 mb-6 w-[70%]"
              maxValue={max}
              minValue={min}
              disabled={isSearchFilterLoading}
            >
              <span className="flex justify-center whitespace-pre font-bold text-white mb-4">
                <Slider.Output />
                {" - "}
                <Slider.Output thumbValue={1} />
                min
              </span>

              <Slider.Label className="sr-only">
                Duration Range Filter
              </Slider.Label>

              <Slider.Track className="relative h-2 bg-purple rounded-full">
                {({ state }) => (
                  <>
                    <div
                      className="block absolute z-10 left-0 h-2 bg-gray-200 rounded-full"
                      style={{
                        width: `${
                          ((state.values[0] - min) / (max - min)) * 100
                        }%`,
                      }}
                    />

                    {state.values.map((_, index) => (
                      <Slider.Thumb
                        name={thumbNamePrefix + index}
                        ariaLabel={`Select duration range ${index + 1}`}
                        className="absolute z-20 w-5 h-5 bg-purple-900 rounded-full border-purple border-4 top-1/2 data-[focus-visible]:bg-green data-[dragging]:bg-purple-700"
                        index={index}
                        key={index}
                      />
                    ))}

                    <div
                      className="block absolute right-0 h-2 bg-gray-200 rounded-full"
                      style={{
                        width: `${
                          100 - ((state.values[1] - min) / (max - min)) * 100
                        }%`,
                      }}
                    />
                  </>
                )}
              </Slider.Track>

              <div className="flex justify-between w-full text-xs font-light text-gray-400 mt-2">
                <span>{min}</span>
                <span>{max}+</span>
              </div>
            </Slider.Root>

            <div className="relative flex flex-row justify-center w-full">
              <FilterFormButton />

              <div className="absolute right-3 bottom-2 text-gray-400 text-xxs md:text-xs">
                For video content only
              </div>
            </div>
          </form>
        )}
      </Popover.Content>
    </Popover.Root>
  );
}

function ActivityTypesFilter({
  isOpen = false,
  disabled,
  onOpen,
  onClose,
  handleSubmit,
}) {
  const alert = Toast.useAlert();
  const {
    searchFilterList,
    isSearchFilterLoading,
    searchFilterError,
  } = useSearchFilterQuery(SEARCH_FILTER_TYPES.ACTIVITY_TYPES);
  const { activeSearchFilter: initialValues = [] } = useActiveSearchFilter(
    SEARCH_FILTER_TYPES.ACTIVITY_TYPES,
  );

  if (searchFilterError) {
    alert.error("Something went wrong. Please reload page.");
    return null;
  }

  return (
    <Popover.Root
      offset={5}
      placement="bottom start"
      shouldFlip={false}
      shouldTriggerOnHover={true}
      restoreFocus={false}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
    >
      <Popover.Trigger
        ariaLabel={`${isOpen ? "Close" : "Open"} Activity Type Filter`}
        disabled={disabled}
        className={twMerge(
          "inline-flex items-center justify-center rounded border border-gray-500 hover:border-mint focus:border-mint transition-colors text-gray-300 text-sm hover:text-mint py-2 px-4 stroke-[#D4D4D8] hover:stroke-mint min-w-fit font-bold capitalize touch-pan-x focus:ring focus:outline-none focus:ring-mint",
          initialValues.length > 0 && "border-purple",
          disabled && "opacity-50 cursor-not-allowed",
        )}
      >
        <div className="h-6 w-6 stroke-2 mr-3 fill-none">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeMiterlimit="10"
              d="M12 3c4.968 0 9 4.032 9 9s-4.032 9-9 9-9-4.032-9-9m9-5c2.758 0 5 2.242 5 5s-2.242 5-5 5-5-2.242-5-5"
            />
          </svg>
        </div>
        activity type
      </Popover.Trigger>

      <Popover.Content className="outline-none w-full overflow-hidden relative md:w-screen-xs border border-purple rounded">
        {isOpen && (
          <form
            className="max-h-screen-xs bg-gray-800 overflow-y-scroll"
            onSubmit={(event) => {
              event.preventDefault();

              const selectedCheckboxes = filter(
                event.target.elements,
                (element) => element.checked,
              );

              const filterActivityTypesValues = Object.keys(
                selectedCheckboxes,
              ).map((key) => Number(selectedCheckboxes[key].value));

              handleSubmit({
                filterValues: filterActivityTypesValues,
                filterType: SEARCH_FILTER_TYPES.ACTIVITY_TYPES,
              });
            }}
          >
            <CheckboxGroup.Root
              defaultValue={initialValues}
              className="grid grid-cols-2 mb-10"
              isDisabled={isSearchFilterLoading}
            >
              {searchFilterList?.map(({ id, name }, index) => (
                <Checkbox.Root
                  key={id}
                  value={id}
                  className={twMerge(
                    "flex items-center pl-2 pr-4 py-4 border-b-[1px] border-gray-500 h-[52px] lg:h-[72px] overflow-hidden",
                    index % 2 === 0 && "border-r-sm",
                  )}
                >
                  <Checkbox.Label
                    className="text-white font-bold cursor-pointer ml-1 text-xs md:text-sm break-words text-ellipsis line-clamp-2"
                    ariaLabel={`Select ${name} activity type`}
                  >
                    {name}
                  </Checkbox.Label>
                </Checkbox.Root>
              ))}
            </CheckboxGroup.Root>

            <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
              <div className="bg-gradient-to-b from-transparent to-gray-800 h-full absolute left-0 right-0" />
              <div className="w-fit mx-auto pointer-events-auto">
                <FilterFormButton />
              </div>
            </div>
          </form>
        )}
      </Popover.Content>
    </Popover.Root>
  );
}

function EnergyLevelsFilter({
  isOpen = false,
  disabled,
  onOpen,
  onClose,
  handleSubmit,
}) {
  const alert = Toast.useAlert();
  const {
    searchFilterList,
    isSearchFilterLoading,
    searchFilterError,
  } = useSearchFilterQuery(SEARCH_FILTER_TYPES.ENERGY_LEVELS);
  const { activeSearchFilter: initialValues = [] } = useActiveSearchFilter(
    SEARCH_FILTER_TYPES.ENERGY_LEVELS,
  );

  if (searchFilterError) {
    alert.error("Something went wrong. Please reload page.");
    return null;
  }

  return (
    <Popover.Root
      offset={5}
      placement="bottom start"
      shouldFlip={false}
      shouldTriggerOnHover={true}
      restoreFocus={false}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
    >
      <Popover.Trigger
        ariaLabel={`${isOpen ? "Close" : "Open"} Energy Level Filter`}
        disabled={disabled}
        className={twMerge(
          "inline-flex items-center justify-center rounded border border-gray-500 hover:border-mint focus:border-mint transition-colors text-gray-300 text-sm hover:text-mint py-2 px-4 stroke-[#D4D4D8] hover:stroke-mint min-w-fit font-bold capitalize touch-pan-x focus:ring focus:outline-none focus:ring-mint",
          initialValues.length > 0 && "border-purple",
          disabled && "opacity-50 cursor-not-allowed",
        )}
      >
        <div className="h-6 w-6 stroke-2 mr-3 fill-none">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path
              strokeMiterlimit="10"
              d="M14.401 5.419l.007-.023.006-.023C14.579 4.713 14.1 4 13.346 4H9.46c-.587 0-.912.418-1.026.7l-.011.03-.01.029-2.35 7.093c-.28.794.371 1.457 1.042 1.457h2.844l-1.36 5.322-.003.011-.002.012c-.143.616.246 1.094.652 1.262.412.17.977.092 1.313-.368l7.223-9.331h0l.008-.011c.596-.792-.06-1.756-.88-1.756h-3.413l.915-3.031z"
            />
          </svg>
        </div>
        energy level
      </Popover.Trigger>

      <Popover.Content className="outline-none overflow-hidden w-full md:w-screen-xs bg-gray-800 border border-purple rounded">
        {isOpen && (
          <form
            onSubmit={(event) => {
              event.preventDefault();

              const selectedCheckboxes = filter(
                event.target.elements,
                (element) => element.checked,
              );
              const filterEnergyLevelsValues = Object.keys(
                selectedCheckboxes,
              ).map((key) => selectedCheckboxes[key].value);

              handleSubmit({
                filterValues: filterEnergyLevelsValues,
                filterType: SEARCH_FILTER_TYPES.ENERGY_LEVELS,
              });
            }}
          >
            <CheckboxGroup.Root
              defaultValue={initialValues}
              className="pl-4 mt-4 mb-6"
              isDisabled={isSearchFilterLoading}
            >
              <div className="flex flex-col items-center">
                <CheckboxGroup.Label className="text-sm font-bold text-white">
                  Levels
                </CheckboxGroup.Label>

                <div className="flex flex-row mt-4">
                  <span className="w-[85px] mr-3 h-10 flex items-center text-center">
                    <p className="text-xs text-gray-100">Energy Down</p>
                  </span>

                  {searchFilterList?.map((energyLevel, index) => (
                    <Checkbox.Root
                      key={energyLevel}
                      variant={Checkbox.variants.custom}
                      name={energyLevel}
                      value={String(energyLevel)}
                      className={index === 1 ? "mx-5" : ""}
                    >
                      <Checkbox.Label
                        aria-label={`Select ${energyLevel} energy level`}
                        className="flex flex-col items-center group select-none cursor-pointer"
                      >
                        <div className="flex justify-center items-center rounded-full bg-gray-700 h-10 w-10 text-sm font-bold text-gray-300 transition-colors duration-200 ease-in-out group-data-[selected=true]:bg-purple group-data-[focused=true]:outline-none group-data-[focused=true]:ring ring-offset-2 ring-offset-gray-700 ring-purple">
                          {energyLevel.charAt(0).toUpperCase()}
                        </div>

                        <span className="text-xs text-gray-300 capitalize font-light mt-2">
                          {energyLevel}
                        </span>
                      </Checkbox.Label>
                    </Checkbox.Root>
                  ))}

                  <span className="w-[85px] ml-3 h-10 flex items-center text-center">
                    <p className="text-xs text-gray-100">Energy Up</p>
                  </span>
                </div>
              </div>
            </CheckboxGroup.Root>

            <div className="relative flex flex-row justify-center w-full">
              {/* Spacer to make sure that Apply button is centered */}
              <div className="w-4" />

              <FilterFormButton />
              <div className="absolute right-1 bottom-2 text-gray-400 text-xxs md:text-xs">
                For video content only
              </div>
            </div>
          </form>
        )}
      </Popover.Content>
    </Popover.Root>
  );
}

function GradeLevelFilter({
  isOpen = false,
  disabled,
  onOpen,
  onClose,
  handleSubmit,
}) {
  const alert = Toast.useAlert();
  const {
    searchFilterList,
    isSearchFilterLoading,
    searchFilterError,
  } = useSearchFilterQuery(SEARCH_FILTER_TYPES.GRADE_LEVELS);
  const { activeSearchFilter: initialValues = [] } = useActiveSearchFilter(
    SEARCH_FILTER_TYPES.GRADE_LEVELS,
  );

  if (searchFilterError) {
    alert.error("Something went wrong. Please reload page.");
    return null;
  }

  return (
    <Popover.Root
      offset={5}
      placement="bottom start"
      shouldFlip={false}
      shouldTriggerOnHover={true}
      restoreFocus={false}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
    >
      <Popover.Trigger
        ariaLabel={`${isOpen ? "Close" : "Open"} Grade Level Filter`}
        disabled={disabled}
        className={twMerge(
          "inline-flex items-center justify-center rounded border border-gray-500 hover:border-mint focus:border-mint transition-colors text-gray-300 text-sm hover:text-mint py-2 px-4 stroke-[#D4D4D8] hover:stroke-mint min-w-fit font-bold capitalize touch-pan-x focus:ring focus:outline-none focus:ring-mint",
          initialValues.length > 0 && "border-purple",
          disabled && "opacity-50 cursor-not-allowed",
        )}
      >
        <div className="h-6 w-6 stroke-2 mr-3 fill-none">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="inherit"
            viewBox="0 0 24 24"
            className="mr-3"
          >
            <mask id="path-1-inside-1_968_11934" fill="inherit">
              <path
                fillRule="evenodd"
                fill="inherit"
                className="hover:fill-mint"
                d="M20.349 5.249h-.001l-.009-.004h.01l-.009-.003h-.001l-5.257-1.997-3.003-1.14c-.37-.14-.78-.14-1.15 0l-8.27 3.14c-.49.19-.75.65-.63 1.11.91 3.26 4.66 15.52 9.47 15.52 4.444 0 7.984-10.467 9.214-14.63.101-.342.187-.642.256-.89.13-.457-.136-.914-.62-1.106zM4.235 6.786l7.269-2.76 7.26 2.757a66.58 66.58 0 01-.98 3.028l-5.836-2.694-.419-.193-.419.193-5.888 2.717a66.575 66.575 0 01-.987-3.048zm1.684 4.93c.36.918.757 1.856 1.188 2.762.794 1.667 1.65 3.116 2.521 4.12.914 1.051 1.544 1.277 1.871 1.277.327 0 .957-.226 1.87-1.278.872-1.003 1.729-2.452 2.522-4.119.435-.914.835-1.86 1.197-2.786l-5.559-2.566-5.61 2.59z"
                clipRule="evenodd"
              />
            </mask>
            <path
              fill="inherit"
              className="hover:fill-mint"
              d="M20.348 5.248l.738-1.858-.008-.004-.73 1.862zm.001 0l.74-1.858h-.002L20.35 5.25zm-.01-.003v-2H9.758l9.851 3.862.73-1.862zm.01 0v2H31.25L21.06 3.375l-.71 1.87zm-.009-.003l.71-1.87-.71 1.87zm-.001 0l-.71 1.869.71-1.87zm-5.257-1.997l.71-1.87-.71 1.87zm-3.003-1.14l.71-1.87h-.002l-.708 1.87zm-1.15 0l-.708-1.87h-.002l.71 1.87zm-8.27 3.14l-.71-1.87-.013.005.723 1.865zm-.63 1.11L.094 6.86l.004.016.005.017 1.926-.538zm18.684.89l-1.918-.567 1.918.567zm.256-.89l-1.925-.544-.001.006 1.926.538zm-9.465-2.329l.71-1.87-.71-.27-.71.27.71 1.87zm-7.269 2.76l-.71-1.87-1.737.66.53 1.78 1.917-.57zm14.53-.003l1.916.57.53-1.78-1.737-.66-.71 1.87zm-.98 3.028l-.839 1.816 1.997.921.729-2.075-1.888-.662zm-5.837-2.694l-.838 1.816.838-1.816zm-.419-.193l.838-1.816-.838-.387-.838.387.838 1.816zm-.419.193l.838 1.816-.838-1.816zM5.222 9.834l-1.887.663.729 2.075 1.996-.922-.838-1.816zm.697 1.882L5.081 9.9l-1.711.79.687 1.755 1.862-.73zm1.188 2.762l1.806-.86-1.806.86zm2.521 4.12l-1.51 1.31 1.51-1.31zm3.742 0l1.51 1.31-1.51-1.31zm2.52-4.12l1.806.86-1.805-.86zm1.198-2.786l1.863.728.686-1.755-1.71-.789-.839 1.816zm-5.559-2.566l.838-1.816-.838-.386-.838.386.838 1.816zm8.08-2.019h.002l1.476-3.717-1.478 3.717zm0 0l.009.003 1.46-3.724-.009-.003-1.46 3.724zm.74-3.862h-.01v4h.01v-4zm-.719 3.866l.01.004 1.419-3.74-.009-.003-1.42 3.74zm0 0l1.42-3.74h-.001l-1.42 3.74zm-5.258-1.996l5.257 1.996 1.42-3.74-5.258-1.996-1.42 3.74zm-3.003-1.14l3.003 1.14 1.42-3.74-3.003-1.14-1.42 3.74zm.268 0a.373.373 0 01-.266 0l1.416-3.74a3.627 3.627 0 00-2.566 0l1.416 3.74zm-8.268 3.14l8.27-3.14-1.42-3.74-8.27 3.14 1.42 3.74zm.595-1.265c.076.29.028.593-.109.835a.952.952 0 01-.473.425L1.936 3.38C.645 3.88-.32 5.272.094 6.86l3.87-1.01zM11.5 19.875c-.327 0-.957-.226-1.87-1.278-.871-1.003-1.728-2.452-2.522-4.119-1.577-3.316-2.706-7.064-3.152-8.66L.103 6.892c.464 1.663 1.665 5.675 3.393 9.304.859 1.806 1.898 3.625 3.112 5.023 1.17 1.348 2.813 2.655 4.891 2.655v-4zm7.296-13.197c-.604 2.046-1.756 5.564-3.267 8.537-.759 1.494-1.557 2.745-2.35 3.595-.822.881-1.382 1.065-1.679 1.065v4c1.925 0 3.474-1.125 4.604-2.336 1.16-1.243 2.156-2.868 2.991-4.512 1.678-3.301 2.911-7.098 3.537-9.215l-3.836-1.134zm.248-.86c-.067.238-.15.528-.248.86l3.836 1.134c.104-.353.193-.662.264-.92l-3.852-1.075zm.567 1.29a.942.942 0 01-.461-.42 1.159 1.159 0 01-.105-.877l3.85 1.088c.461-1.634-.566-3.015-1.806-3.509L19.61 7.107zm-8.816-4.952l-7.269 2.76 1.42 3.74 7.269-2.76-1.42-3.74zm8.68 2.757l-7.26-2.757-1.42 3.74 7.26 2.756 1.42-3.74zm.198 5.56a68.657 68.657 0 001.01-3.12l-3.835-1.14a64.634 64.634 0 01-.95 2.936l3.775 1.324zm-8.562-1.54l5.836 2.694 1.676-3.632L12.786 5.3 11.11 8.933zm-.42-.193l.42.193 1.676-3.632-.419-.193-1.676 3.632zm1.258.193l.42-.193-1.677-3.632-.42.193 1.677 3.632zM6.06 11.65l5.888-2.717-1.676-3.632L4.384 8.02 6.06 11.65zM2.318 7.356c.253.85.596 1.941 1.017 3.141L7.11 9.172a64.554 64.554 0 01-.957-2.956l-3.834 1.14zm1.739 5.088c.373.953.789 1.936 1.245 2.893l3.611-1.718a40.64 40.64 0 01-1.131-2.633l-3.725 1.458zm1.245 2.893c.826 1.736 1.774 3.371 2.816 4.571l3.02-2.622c-.699-.805-1.464-2.07-2.225-3.667l-3.611 1.719zm2.816 4.571c1.05 1.209 2.185 1.967 3.381 1.967v-4c.29 0 .404.095.315.041-.085-.05-.315-.215-.675-.63l-3.02 2.622zm3.381 1.967c1.196 0 2.332-.758 3.381-1.967l-3.02-2.622c-.36.415-.59.58-.676.63-.089.054.026-.041.315-.041v4zm3.381-1.967c1.042-1.2 1.99-2.835 2.816-4.57l-3.611-1.72c-.76 1.598-1.526 2.863-2.225 3.668l3.02 2.622zm2.816-4.57c.46-.966.88-1.958 1.255-2.918l-3.726-1.456c-.348.891-.73 1.793-1.14 2.655l3.611 1.719zm.23-5.462L12.367 7.31l-1.676 3.632 5.559 2.566 1.676-3.632zM10.691 7.31L5.08 9.9l1.676 3.631 5.61-2.589-1.676-3.632z"
              mask="url(#path-1-inside-1_968_11934)"
            />
          </svg>
        </div>
        grade level
      </Popover.Trigger>

      <Popover.Content className="outline-none w-full overflow-hidden relative md:w-screen-xs border border-purple rounded">
        {isOpen && (
          <form
            className="max-h-screen-xs bg-gray-800 overflow-y-scroll"
            onSubmit={(event) => {
              event.preventDefault();

              const selectedCheckboxes = filter(
                event.target.elements,
                (element) => element.checked,
              );
              const filterGradeLevelsValues = Object.keys(
                selectedCheckboxes,
              ).map((key) => Number(selectedCheckboxes[key].value));

              handleSubmit({
                filterValues: filterGradeLevelsValues,
                filterType: SEARCH_FILTER_TYPES.GRADE_LEVELS,
              });
            }}
          >
            <CheckboxGroup.Root
              defaultValue={initialValues}
              className="grid grid-cols-2 mb-10"
              isDisabled={isSearchFilterLoading}
            >
              {searchFilterList?.map(({ id, name }, index) => (
                <Checkbox.Root
                  key={id}
                  value={id}
                  className={twMerge(
                    "flex items-center pl-2 pr-4 py-4 border-b-[1px] border-gray-500 h-[52px] lg:h-[72px] overflow-hidden",
                    index % 2 === 0 && "border-r-sm",
                  )}
                >
                  <Checkbox.Label
                    className="text-white font-bold cursor-pointer ml-1 text-xs md:text-sm break-words text-ellipsis line-clamp-2"
                    ariaLabel={`Select ${name} grade level`}
                  >
                    {name}
                  </Checkbox.Label>
                </Checkbox.Root>
              ))}
            </CheckboxGroup.Root>

            <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
              <div className="bg-gradient-to-b from-transparent to-gray-800 h-full absolute left-0 right-0" />
              <div className="w-fit mx-auto pointer-events-auto">
                <FilterFormButton />
              </div>
            </div>
          </form>
        )}
      </Popover.Content>
    </Popover.Root>
  );
}

function ChannelFilter({
  isOpen = false,
  disabled,
  onOpen,
  onClose,
  handleSubmit,
}) {
  const alert = Toast.useAlert();
  const {
    searchFilterList: featuredChannel,
    isSearchFilterLoading: isSearchFilterFeaturedChannelLoading,
    searchFilterError: searchFilterFeaturedChannelError,
  } = useSearchFilterQuery(SEARCH_FILTER_TYPES.FEATURED_CHANNEL);
  const {
    searchFilterList,
    isSearchFilterLoading,
    searchFilterError,
  } = useSearchFilterQuery(SEARCH_FILTER_TYPES.CHANNELS);
  const { activeSearchFilter: initialValues = [] } = useActiveSearchFilter(
    SEARCH_FILTER_TYPES.CHANNELS,
  );

  if (searchFilterError || searchFilterFeaturedChannelError) {
    alert.error("Something went wrong. Please reload page.");
    return null;
  }

  return (
    <Popover.Root
      offset={5}
      placement="bottom start"
      shouldFlip={false}
      shouldTriggerOnHover={true}
      restoreFocus={false}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
    >
      <Popover.Trigger
        ariaLabel={`${isOpen ? "Close" : "Open"} Channels Filter`}
        disabled={disabled}
        className={twMerge(
          "inline-flex items-center justify-center rounded border border-gray-500 hover:border-mint focus:border-mint transition-colors text-gray-300 text-sm hover:text-mint py-2 px-4 stroke-[#D4D4D8] hover:stroke-mint min-w-fit font-bold capitalize touch-pan-x focus:ring focus:outline-none focus:ring-mint",
          initialValues.length > 0 && "border-purple",
          disabled && "opacity-50 cursor-not-allowed",
        )}
      >
        <div className="w-6 h-6 mr-3 fill-none stroke-2">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeMiterlimit="10"
              d="M7 20s2.725-1 5.5-1 5.5 1 5.5 1M4.706 4h14.588C20.236 4 21 4.811 21 5.812v9.376c0 1-.764 1.812-1.706 1.812H4.706C3.764 17 3 16.189 3 15.188V5.812C3 4.812 3.764 4 4.706 4z"
            />
          </svg>
        </div>
        channels
      </Popover.Trigger>

      <Popover.Content className="outline-none w-full overflow-hidden relative md:w-screen-xs border border-purple rounded">
        {isOpen && (
          <form
            className="max-h-screen-xs bg-gray-800 overflow-y-scroll"
            onSubmit={(event) => {
              event.preventDefault();

              const selectedCheckboxes = filter(
                event.target.elements,
                (element) => element.checked,
              );

              const selectedValues = [
                ...new Set(
                  Object.keys(selectedCheckboxes).map((key) =>
                    Number(selectedCheckboxes[key].value),
                  ),
                ),
              ];

              handleSubmit({
                filterValues: selectedValues,
                filterType: SEARCH_FILTER_TYPES.CHANNELS,
              });
            }}
          >
            <CheckboxGroup.Root
              defaultValue={initialValues}
              className="grid grid-cols-2 mb-10"
              isDisabled={
                isSearchFilterLoading || isSearchFilterFeaturedChannelLoading
              }
            >
              {featuredChannel && (
                <div className="col-span-2 flex flex-col">
                  <div className="flex justify-center items-center bg-gray-900 py-3">
                    <StarIcon className="h-5 w-5 mr-2 text-yellow" />
                    <span className="uppercase font-bold text-white">
                      featured channel
                    </span>
                  </div>

                  <Checkbox.Root
                    value={featuredChannel.id}
                    className="flex flex-row items-center pl-2 pr-4 py-4 h-[52px] lg:h-[72px] w-full overflow-hidden"
                  >
                    <Checkbox.Label
                      className="flex flex-row items-center cursor-pointer select-none ml-1"
                      ariaLabel={`Select ${featuredChannel.name} channel`}
                    >
                      <Image
                        sources={featuredChannel.image}
                        alt={featuredChannel.name}
                        className="h-[40px] w-[40px] object-cover"
                      />

                      <span className="text-white font-bold cursor-pointer ml-3 text-xs md:text-sm break-words text-ellipsis line-clamp-2">
                        {featuredChannel.name}
                      </span>

                      {featuredChannel.sponsorLogo && (
                        <div className="ml-auto flex flex-row items-center">
                          <span className="text-white border-l-[1px] border-gray-500 px-2 text-xxs">
                            powered by
                          </span>

                          <Image
                            sources={featuredChannel.sponsorLogo}
                            alt={featuredChannel.name}
                            className="max-h-[32px] min-w-[32px] max-w-[80px] md:max-w-[120px] object-cover"
                          />
                        </div>
                      )}
                    </Checkbox.Label>
                  </Checkbox.Root>
                </div>
              )}

              <div className="col-span-2 text-center text-white bg-gray-900 py-3 font-bold uppercase">
                all channels
              </div>

              {searchFilterList?.map(({ id, name, image }, index) => (
                <Checkbox.Root
                  key={id}
                  value={id}
                  className={twMerge(
                    "flex items-center pl-2 pr-4 py-4 border-b-[1px] border-gray-500 h-[52px] lg:h-[72px] overflow-hidden",
                    index % 2 === 0 && "border-r-sm",
                  )}
                >
                  <Checkbox.Label
                    className="flex flex-row items-center cursor-pointer ml-1 overflow-hidden"
                    ariaLabel={`Select ${name} grade level`}
                  >
                    <Image
                      sources={image}
                      alt={name}
                      className="h-[40px] w-[40px] object-cover"
                    />
                    <span className="text-white font-bold cursor-pointer ml-3 text-xs md:text-sm break-words text-ellipsis line-clamp-2">
                      {name}
                    </span>
                  </Checkbox.Label>
                </Checkbox.Root>
              ))}
            </CheckboxGroup.Root>

            <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
              <div className="bg-gradient-to-b from-transparent to-gray-800 h-full absolute left-0 right-0" />
              <div className="w-fit mx-auto pointer-events-auto">
                <FilterFormButton />
              </div>
            </div>
          </form>
        )}
      </Popover.Content>
    </Popover.Root>
  );
}

// Function to extract the ID part from a string
function extractPrimaryIdentifier(inputString) {
  const parts = inputString.split("_", 1);
  return parts[0];
}

// Function to extract the type part from a string
function extractSecondaryIdentifier(inputString) {
  const parts = inputString.split("_");
  return parts[1];
}

// Function to merge ID and type into a single string
function mergeIdentifierWithType(identifier, type) {
  return `${identifier}_${type}`;
}

function CharacterFilter({
  isOpen = false,
  disabled,
  onOpen,
  onClose,
  handleSubmit,
}) {
  const alert = Toast.useAlert();
  const {
    searchFilterList,
    isSearchFilterLoading,
    searchFilterError,
  } = useSearchFilterQuery(SEARCH_FILTER_TYPES.CHARACTERS);
  const {
    activeSearchFilter: currentSelectedCharacters = [],
  } = useActiveSearchFilter(SEARCH_FILTER_TYPES.CHARACTERS);

  const {
    activeSearchFilter: currentSelectedChampions = [],
  } = useActiveSearchFilter(SEARCH_FILTER_TYPES.CHAMPIONS);

  if (searchFilterError) {
    alert.error("Something went wrong. Please reload page.");
    return null;
  }

  return (
    <Popover.Root
      offset={5}
      placement="bottom start"
      shouldFlip={false}
      shouldTriggerOnHover={true}
      restoreFocus={false}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
    >
      <Popover.Trigger
        ariaLabel={`${isOpen ? "Close" : "Open"} Characters Filter`}
        disabled={disabled}
        className={twMerge(
          "inline-flex items-center justify-center rounded border border-gray-500 hover:border-mint focus:border-mint transition-colors text-gray-300 text-sm hover:text-mint py-2 px-4 stroke-[#D4D4D8] hover:stroke-mint min-w-fit font-bold capitalize touch-pan-x focus:ring focus:outline-none focus:ring-mint",
          (currentSelectedCharacters.length > 0 ||
            currentSelectedChampions.length > 0) &&
            "border-purple",
          disabled && "opacity-50 cursor-not-allowed",
        )}
      >
        <div className="mr-3 stroke-[3] fill-none w-6 h-6">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <circle cx="16.708" cy="9.704" r="4.566" />
            <circle cx="6.888" cy="13.748" r="3.988" />
            <path
              strokeLinecap="round"
              d="M14.373 17.963c1.333.964 4.8 2.12 8-.963M9.098 3.645c-.97-.036-3.226.434-4.49 2.607"
            />
          </svg>
        </div>
        characters
      </Popover.Trigger>

      <Popover.Content className="outline-none w-full overflow-hidden relative md:w-screen-xs border border-purple rounded">
        {isOpen && (
          <form
            className="max-h-screen-xs bg-gray-800 overflow-y-scroll"
            onSubmit={(event) => {
              const selectedCharacters = [];
              const selectedChampions = [];

              event.preventDefault();

              Array.from(event.target.elements).forEach(
                ({ checked, value }) => {
                  if (checked) {
                    const type = extractSecondaryIdentifier(value);
                    const id = extractPrimaryIdentifier(value);

                    if (type === CONTENT_TYPES.CHARACTER) {
                      selectedCharacters.push(Number(id));
                    } else if (type === CONTENT_TYPES.CHAMPION) {
                      selectedChampions.push(Number(id));
                    }
                  }
                },
              );

              handleSubmit({
                filterValues: {
                  [SEARCH_FILTER_TYPES.CHARACTERS]: selectedCharacters,
                  [SEARCH_FILTER_TYPES.CHAMPIONS]: selectedChampions,
                },
                filterType: [
                  SEARCH_FILTER_TYPES.CHARACTERS,
                  SEARCH_FILTER_TYPES.CHAMPIONS,
                ],
              });
            }}
          >
            <CheckboxGroup.Root
              defaultValue={[
                ...currentSelectedCharacters.map((id) =>
                  mergeIdentifierWithType(id, CONTENT_TYPES.CHARACTER),
                ),
                ...currentSelectedChampions.map((id) =>
                  mergeIdentifierWithType(id, CONTENT_TYPES.CHAMPION),
                ),
              ]}
              className="grid grid-cols-2 mb-10"
              isDisabled={isSearchFilterLoading}
            >
              {searchFilterList?.map(({ id, name, image, type }, index) => (
                <div
                  key={id}
                  className={twMerge(
                    "flex items-center pr-4 border-b-[1px] border-gray-500 h-[52px] lg:h-[72px] overflow-hidden",
                    index % 2 === 0 && "border-r-sm",
                  )}
                >
                  <Image
                    sources={image}
                    alt={name}
                    className="h-full w-[40px] object-cover"
                  />

                  <Checkbox.Root
                    value={mergeIdentifierWithType(id, type)}
                    className="flex items-center overflow-hidden ml-3"
                  >
                    <Checkbox.Label
                      className="text-white font-bold cursor-pointer ml-1 text-xs md:text-sm break-words text-ellipsis line-clamp-2"
                      ariaLabel={`Select ${name} character`}
                    >
                      {name}
                    </Checkbox.Label>
                  </Checkbox.Root>
                </div>
              ))}
            </CheckboxGroup.Root>

            <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
              <div className="bg-gradient-to-b from-transparent to-gray-800 h-full absolute left-0 right-0" />
              <div className="w-fit mx-auto pointer-events-auto">
                <FilterFormButton />
              </div>
            </div>
          </form>
        )}
      </Popover.Content>
    </Popover.Root>
  );
}

const NoResultsState = () => (
  <div className="container flex flex-1 my-10">
    <div className="flex flex-col flex-1 justify-center items-center rounded-lg py-14">
      <NextImage
        src={`${URLS.GN_ASSETS_BASE_URL}/web_public/images/no-results-search-champ.svg`}
        alt="Sad Squatchy"
        width={276}
        height={200}
        className="mb-4"
      />
      <h3 className="text-white text-md text-center font-bold mb-2">
        Sorry, there are no search results available
      </h3>
      <p className="text-white text-sm text-center">
        Try searching one more time with the options available above.
      </p>
    </div>
  </div>
);

const EmptyState = () => (
  <div className="container flex flex-1 my-10">
    <div className="flex flex-col flex-1 justify-center items-center rounded-lg py-14">
      <NextImage
        src={`${URLS.GN_ASSETS_BASE_URL}/web_public/images/empty-state-search-champ.svg`}
        alt="Zapp Von Doubler"
        width={146}
        height={200}
        className="mb-4"
      />
      <h3 className="text-white text-md text-center font-bold mb-2">
        Fun content is waiting for you!
      </h3>
      <p className="text-white text-sm text-center">
        Try one of the button options above or <br />
        type in the search bar.
      </p>
    </div>
  </div>
);

const FilterFormButton = () => (
  <Pressable
    className="uppercase bg-purple px-8 py-1 font-bold [clip-path:polygon(10%_0%,90%_0%,100%_100%,0%_100%)] text-white ring ring-blue-300"
    buttonType="submit"
  >
    apply
  </Pressable>
);

const FilterPill = ({ id, filterLabel, filterType, icon, removeFilter }) => (
  <div className="flex items-center w-fit bg-purple rounded-full py-2 px-4 space-x-4">
    <div className="border-r-[1px] pr-2">{icon}</div>
    <span className="capitalize">{filterLabel}</span>
    <Button
      className="w-4 h-4"
      onPress={() => {
        removeFilter(id, filterType);
      }}
    >
      <svg
        width="11"
        height="11"
        viewBox="0 0 11 11"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1.00293 9.01172L9.00293 1.01172M1.00293 1.01172L9.00293 9.01172"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Button>
  </div>
);

const SearchResultCard = ({
  analyticsProps: { sourceElement },
  card: {
    type,
    id,
    slug,
    title,
    description,
    images,
    attribution,
    duration,
    ...card
  },
}) => (
  <li key={id} className="flex flex-col justify-top relative group">
    {type === "BlogPost" ? (
      <BlogCardTile
        slug={slug}
        name={title}
        description={description}
        imageUrl={images.tile}
        title={title}
        analyticsProps={{
          sourcePage: PAGES.SEARCH,
          sourcePageType: PAGES.SEARCH,
          sourceName: "search",
          sourceElement,
        }}
      />
    ) : type === "VideoVersion" ? (
      <VideoTile
        id={id}
        slug={slug}
        title={title}
        description={description}
        images={images}
        duration={duration}
        favorite={card.favorite}
        sponsor={{
          expiration: attribution?.expiration,
          type: attribution?.type,
        }}
        impressionsProps={{
          sourceName: title,
          sourcePage: PAGES.SEARCH,
          sourcePageType: PAGES.SEARCH,
          sourceElement,
        }}
        linkParams={{
          sourcePage: PAGES.SEARCH,
          sourcePageType: PAGES.SEARCH,
          sourceName: "search",
          sourceElement,
        }}
      />
    ) : type === "Printable" ? (
      <PrintableTile
        id={id}
        slug={slug}
        mode="dark"
        sources={
          card.heroImage["thumb@1x"]
            ? {
                "regular@1x": card.heroImage["thumb@1x"],
                "regular@2x": card.heroImage["thumb@2x"],
                "regular@3x": card.heroImage["thumb@3x"],
              }
            : card.image
        }
        title={title}
        description={description}
        format="landscape"
        linkParams={{
          sourcePage: PAGES.SEARCH,
          sourcePageType: PAGES.SEARCH,
          sourceName: "search",
          sourceElement,
        }}
      />
    ) : [
        CONTENT_TYPES.TOPIC,
        CONTENT_TYPES.CHANNEL,
        CONTENT_TYPES.TAG,
        "TopicContentTag",
      ].includes(type) ? (
      <TextTile
        id={id}
        slug={slug}
        name={card.name}
        title={card.name}
        description={description}
        tile={
          card.image || card.images?.spotlightImage || card.images?.tileImage
        }
        format="landscape"
        linkParams={{
          sourcePage: PAGES.SEARCH,
          sourcePageType: PAGES.SEARCH,
          sourceName: "search",
          sourceElement,
        }}
      />
    ) : null}
  </li>
);

const Search = () => {
  const SEARCH_BY_FILTERS_PAGE_SIZE = 20;
  const router = useRouter();
  const alert = Toast.useAlert();
  const fuzzySearchTerm = router.query?.searchTerm
    ? decodeURIComponent(router.query.searchTerm)
    : "";
  const [activeFilter, setActiveFilter] = React.useState({});
  const {
    searchFilters,
    isSearchFiltersLoading,
    searchFiltersError,
  } = useSearchFiltersQuery();
  const {
    activeSearchFilters,
    setActiveSearchFilters,
  } = useActiveSearchFilters();

  const {
    data: searchByFiltersResponse = { pages: [{}] },
    isInitialLoading: searchByFiltersIsLoading,
    hasNextPage: searchByFiltersHasNextPage,
    fetchNextPage: searchByFiltersFetchNextPage,
    isFetchingNextPage: searchByFiltersIsFetchingNextPage,
  } = useInfiniteQuery({
    queryFn: ({ pageParam }) =>
      client.search({
        page: pageParam?.page || 1,
        pageSize: SEARCH_BY_FILTERS_PAGE_SIZE,
        filters: activeSearchFilters,
        fuzzySearchTerm,
      }),
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.currentPage + 1;
      return nextPage <= lastPage.totalPages ? { page: nextPage } : undefined;
    },
    enabled: !isEmpty(activeSearchFilters) || !isEmpty(fuzzySearchTerm),
    staleTime: Infinity,
    queryKey: [JSURL.stringify(activeSearchFilters), fuzzySearchTerm],
  });

  const { pages: searchByFiltersPages } = searchByFiltersResponse;
  const [
    {
      results: searchByFiltersResults,
      totalResults: searchByFiltersTotalResults,
      totalPages: searchByFiltersTotalPages,
    },
  ] = searchByFiltersPages;

  const numberOfResultsDisplayed =
    searchByFiltersResults &&
    searchByFiltersResults.length > 0 &&
    !searchByFiltersIsLoading
      ? `${
          searchByFiltersTotalPages === searchByFiltersPages.length
            ? searchByFiltersTotalResults
            : searchByFiltersPages.length * SEARCH_BY_FILTERS_PAGE_SIZE
        } of ${searchByFiltersTotalResults}`
      : "";

  const handleFilterSubmit = ({ filterValues, filterType }) => {
    setActiveFilter({});
    setActiveSearchFilters({ filterType, filterValues });
  };

  const removeFilter = (id, filterType) => {
    if (filterType === SEARCH_FILTER_TYPES.DURATION) {
      setActiveSearchFilters({
        filterType,
        filterValues: null,
      });
      return;
    }

    setActiveSearchFilters({
      filterType,
      filterValues: activeSearchFilters[filterType].filter(
        (value) => value !== id,
      ),
    });
  };

  const showClearAllButton = React.useMemo(() => {
    let appliedFilters = Object.values(activeSearchFilters).reduce(
      (filtersSum, filterValues) => filtersSum + filterValues.length,
      0,
    );

    if (activeSearchFilters[SEARCH_FILTER_TYPES.DURATION]) appliedFilters -= 1;
    return appliedFilters > 1;
  }, [activeSearchFilters]);

  const analyticsSourceElement = React.useMemo(
    () =>
      !isEmpty(activeSearchFilters)
        ? fuzzySearchTerm
          ? `${KEYWORD_SEARCH_TYPE}+${FILTERS_SEARCH_TYPE}`
          : FILTERS_SEARCH_TYPE
        : fuzzySearchTerm
        ? KEYWORD_SEARCH_TYPE
        : "",
    [activeSearchFilters, fuzzySearchTerm],
  );

  if (searchFiltersError) {
    alert.error("Something went wrong. Please reload page.");
    return null;
  }

  return (
    <Layout
      canonicalLinkPath={ROUTE_PATHS.SEARCH}
      pageDescription="Search Page"
      pageTitle="Search"
    >
      <div className="flex flex-col flex-1 bg-gradient-to-b from-gray-900 via-gray-900 to-gray-800">
        <div
          className="bg-repeat text-white"
          style={{
            backgroundImage: `url(${URLS.GN_ASSETS_BASE_URL}/web_public/images/background-maze.svg)`,
          }}
        >
          <div className="container flex flex-col py-10 justify-center items-center">
            <KeywordsFilter
              value={fuzzySearchTerm}
              onSubmit={(value) => {
                const newQuery = { ...router.query };
                if (value) {
                  newQuery.searchTerm = encodeURIComponent(value);
                } else {
                  delete newQuery.searchTerm;
                }

                router.replace(constructSearchUrl(newQuery), undefined, {
                  shallow: true,
                });
              }}
            />

            <div className="relative flex flex-row flex-nowrap w-full justify-start gap-4 overflow-x-auto pt-4 md:pt-6">
              <DurationFilter
                isOpen={activeFilter === SEARCH_FILTER_TYPES.DURATION}
                disabled={isSearchFiltersLoading}
                onOpen={() => setActiveFilter(SEARCH_FILTER_TYPES.DURATION)}
                onClose={() => setActiveFilter(undefined)}
                handleSubmit={handleFilterSubmit}
              />

              <ActivityTypesFilter
                isOpen={activeFilter === SEARCH_FILTER_TYPES.ACTIVITY_TYPES}
                disabled={isSearchFiltersLoading}
                onOpen={() =>
                  setActiveFilter(SEARCH_FILTER_TYPES.ACTIVITY_TYPES)
                }
                onClose={() => setActiveFilter(undefined)}
                handleSubmit={handleFilterSubmit}
              />

              <EnergyLevelsFilter
                isOpen={activeFilter === SEARCH_FILTER_TYPES.ENERGY_LEVELS}
                disabled={isSearchFiltersLoading}
                onOpen={() =>
                  setActiveFilter(SEARCH_FILTER_TYPES.ENERGY_LEVELS)
                }
                onClose={() => setActiveFilter(undefined)}
                handleSubmit={handleFilterSubmit}
              />

              <GradeLevelFilter
                isOpen={activeFilter === SEARCH_FILTER_TYPES.GRADE_LEVELS}
                disabled={isSearchFiltersLoading}
                onOpen={() => setActiveFilter(SEARCH_FILTER_TYPES.GRADE_LEVELS)}
                onClose={() => setActiveFilter(undefined)}
                handleSubmit={handleFilterSubmit}
              />

              <ChannelFilter
                isOpen={activeFilter === SEARCH_FILTER_TYPES.CHANNELS}
                disabled={isSearchFiltersLoading}
                onOpen={() => setActiveFilter(SEARCH_FILTER_TYPES.CHANNELS)}
                onClose={() => setActiveFilter(undefined)}
                handleSubmit={handleFilterSubmit}
              />

              <CharacterFilter
                isOpen={activeFilter === SEARCH_FILTER_TYPES.CHARACTERS}
                disabled={isSearchFiltersLoading}
                onOpen={() => setActiveFilter(SEARCH_FILTER_TYPES.CHARACTERS)}
                onClose={() => setActiveFilter(undefined)}
                handleSubmit={handleFilterSubmit}
              />
            </div>
          </div>
        </div>

        <div className="container mt-6">
          <div className="flex items-center justify-between flex-wrap lg:flex-nowrap gap-4 text-white">
            {!isEmpty(activeSearchFilters) && (
              <>
                <div className="order-1">
                  <FilterIcon className="w-6 h-6" />
                </div>

                <div className="flex flex-wrap gap-4 order-last w-full lg:order-2">
                  {activeSearchFilters?.duration?.length ? (
                    <FilterPill
                      icon={<ClockIcon className="w-6 h-6 stroke-[#D4D4D8]" />}
                      filterLabel={`${activeSearchFilters?.duration[0]}-${activeSearchFilters?.duration[1]}min`}
                      removeFilter={removeFilter}
                      filterType={SEARCH_FILTER_TYPES.DURATION}
                    />
                  ) : null}

                  {activeSearchFilters?.activityTypes?.map((id) => {
                    const label = searchFilters?.activityTypes?.find(
                      (activityType) => activityType.id === id,
                    )?.name;
                    return (
                      <FilterPill
                        key={id}
                        id={id}
                        icon={
                          <div className="h-6 w-6 stroke-2 stroke-[#D4D4D8] fill-none">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeMiterlimit="10"
                                d="M12 3c4.968 0 9 4.032 9 9s-4.032 9-9 9-9-4.032-9-9m9-5c2.758 0 5 2.242 5 5s-2.242 5-5 5-5-2.242-5-5"
                              />
                            </svg>
                          </div>
                        }
                        filterLabel={label}
                        removeFilter={removeFilter}
                        filterType={SEARCH_FILTER_TYPES.ACTIVITY_TYPES}
                      />
                    );
                  })}

                  {activeSearchFilters?.energyLevels?.map((id) => (
                    <FilterPill
                      key={id}
                      id={id}
                      icon={
                        <div className="stroke-[#D4D4D8] h-6 w-6 stroke-2 fill-none">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeMiterlimit="10"
                              d="M14.401 5.419l.007-.023.006-.023C14.579 4.713 14.1 4 13.346 4H9.46c-.587 0-.912.418-1.026.7l-.011.03-.01.029-2.35 7.093c-.28.794.371 1.457 1.042 1.457h2.844l-1.36 5.322-.003.011-.002.012c-.143.616.246 1.094.652 1.262.412.17.977.092 1.313-.368l7.223-9.331h0l.008-.011c.596-.792-.06-1.756-.88-1.756h-3.413l.915-3.031z"
                            />
                          </svg>
                        </div>
                      }
                      filterLabel={id}
                      removeFilter={removeFilter}
                      filterType={SEARCH_FILTER_TYPES.ENERGY_LEVELS}
                    />
                  ))}
                  {activeSearchFilters?.gradeLevels?.map((id) => {
                    const label = searchFilters?.gradeLevels?.find(
                      (gradeLevel) => gradeLevel.id === id,
                    )?.name;
                    return (
                      <FilterPill
                        key={id}
                        id={id}
                        icon={
                          <div className="stroke-[#D4D4D8] w-6 h-6 fill-none stroke-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                            >
                              <mask id="path-1-inside-1_968_11934" fill="#fff">
                                <path
                                  fillRule="evenodd"
                                  d="M20.349 5.249h-.001l-.009-.004h.01l-.009-.003h-.001l-5.257-1.997-3.003-1.14c-.37-.14-.78-.14-1.15 0l-8.27 3.14c-.49.19-.75.65-.63 1.11.91 3.26 4.66 15.52 9.47 15.52 4.444 0 7.984-10.467 9.214-14.63.101-.342.187-.642.256-.89.13-.457-.136-.914-.62-1.106zM4.235 6.786l7.269-2.76 7.26 2.757a66.58 66.58 0 01-.98 3.028l-5.836-2.694-.419-.193-.419.193-5.888 2.717a66.575 66.575 0 01-.987-3.048zm1.684 4.93c.36.918.757 1.856 1.188 2.762.794 1.667 1.65 3.116 2.521 4.12.914 1.051 1.544 1.277 1.871 1.277.327 0 .957-.226 1.87-1.278.872-1.003 1.729-2.452 2.522-4.119.435-.914.835-1.86 1.197-2.786l-5.559-2.566-5.61 2.59z"
                                  clipRule="evenodd"
                                />
                              </mask>
                              <path
                                fill="white"
                                d="M20.348 5.248l.738-1.858-.008-.004-.73 1.862zm.001 0l.74-1.858h-.002L20.35 5.25zm-.01-.003v-2H9.758l9.851 3.862.73-1.862zm.01 0v2H31.25L21.06 3.375l-.71 1.87zm-.009-.003l.71-1.87-.71 1.87zm-.001 0l-.71 1.869.71-1.87zm-5.257-1.997l.71-1.87-.71 1.87zm-3.003-1.14l.71-1.87h-.002l-.708 1.87zm-1.15 0l-.708-1.87h-.002l.71 1.87zm-8.27 3.14l-.71-1.87-.013.005.723 1.865zm-.63 1.11L.094 6.86l.004.016.005.017 1.926-.538zm18.684.89l-1.918-.567 1.918.567zm.256-.89l-1.925-.544-.001.006 1.926.538zm-9.465-2.329l.71-1.87-.71-.27-.71.27.71 1.87zm-7.269 2.76l-.71-1.87-1.737.66.53 1.78 1.917-.57zm14.53-.003l1.916.57.53-1.78-1.737-.66-.71 1.87zm-.98 3.028l-.839 1.816 1.997.921.729-2.075-1.888-.662zm-5.837-2.694l-.838 1.816.838-1.816zm-.419-.193l.838-1.816-.838-.387-.838.387.838 1.816zm-.419.193l.838 1.816-.838-1.816zM5.222 9.834l-1.887.663.729 2.075 1.996-.922-.838-1.816zm.697 1.882L5.081 9.9l-1.711.79.687 1.755 1.862-.73zm1.188 2.762l1.806-.86-1.806.86zm2.521 4.12l-1.51 1.31 1.51-1.31zm3.742 0l1.51 1.31-1.51-1.31zm2.52-4.12l1.806.86-1.805-.86zm1.198-2.786l1.863.728.686-1.755-1.71-.789-.839 1.816zm-5.559-2.566l.838-1.816-.838-.386-.838.386.838 1.816zm8.08-2.019h.002l1.476-3.717-1.478 3.717zm0 0l.009.003 1.46-3.724-.009-.003-1.46 3.724zm.74-3.862h-.01v4h.01v-4zm-.719 3.866l.01.004 1.419-3.74-.009-.003-1.42 3.74zm0 0l1.42-3.74h-.001l-1.42 3.74zm-5.258-1.996l5.257 1.996 1.42-3.74-5.258-1.996-1.42 3.74zm-3.003-1.14l3.003 1.14 1.42-3.74-3.003-1.14-1.42 3.74zm.268 0a.373.373 0 01-.266 0l1.416-3.74a3.627 3.627 0 00-2.566 0l1.416 3.74zm-8.268 3.14l8.27-3.14-1.42-3.74-8.27 3.14 1.42 3.74zm.595-1.265c.076.29.028.593-.109.835a.952.952 0 01-.473.425L1.936 3.38C.645 3.88-.32 5.272.094 6.86l3.87-1.01zM11.5 19.875c-.327 0-.957-.226-1.87-1.278-.871-1.003-1.728-2.452-2.522-4.119-1.577-3.316-2.706-7.064-3.152-8.66L.103 6.892c.464 1.663 1.665 5.675 3.393 9.304.859 1.806 1.898 3.625 3.112 5.023 1.17 1.348 2.813 2.655 4.891 2.655v-4zm7.296-13.197c-.604 2.046-1.756 5.564-3.267 8.537-.759 1.494-1.557 2.745-2.35 3.595-.822.881-1.382 1.065-1.679 1.065v4c1.925 0 3.474-1.125 4.604-2.336 1.16-1.243 2.156-2.868 2.991-4.512 1.678-3.301 2.911-7.098 3.537-9.215l-3.836-1.134zm.248-.86c-.067.238-.15.528-.248.86l3.836 1.134c.104-.353.193-.662.264-.92l-3.852-1.075zm.567 1.29a.942.942 0 01-.461-.42 1.159 1.159 0 01-.105-.877l3.85 1.088c.461-1.634-.566-3.015-1.806-3.509L19.61 7.107zm-8.816-4.952l-7.269 2.76 1.42 3.74 7.269-2.76-1.42-3.74zm8.68 2.757l-7.26-2.757-1.42 3.74 7.26 2.756 1.42-3.74zm.198 5.56a68.657 68.657 0 001.01-3.12l-3.835-1.14a64.634 64.634 0 01-.95 2.936l3.775 1.324zm-8.562-1.54l5.836 2.694 1.676-3.632L12.786 5.3 11.11 8.933zm-.42-.193l.42.193 1.676-3.632-.419-.193-1.676 3.632zm1.258.193l.42-.193-1.677-3.632-.42.193 1.677 3.632zM6.06 11.65l5.888-2.717-1.676-3.632L4.384 8.02 6.06 11.65zM2.318 7.356c.253.85.596 1.941 1.017 3.141L7.11 9.172a64.554 64.554 0 01-.957-2.956l-3.834 1.14zm1.739 5.088c.373.953.789 1.936 1.245 2.893l3.611-1.718a40.64 40.64 0 01-1.131-2.633l-3.725 1.458zm1.245 2.893c.826 1.736 1.774 3.371 2.816 4.571l3.02-2.622c-.699-.805-1.464-2.07-2.225-3.667l-3.611 1.719zm2.816 4.571c1.05 1.209 2.185 1.967 3.381 1.967v-4c.29 0 .404.095.315.041-.085-.05-.315-.215-.675-.63l-3.02 2.622zm3.381 1.967c1.196 0 2.332-.758 3.381-1.967l-3.02-2.622c-.36.415-.59.58-.676.63-.089.054.026-.041.315-.041v4zm3.381-1.967c1.042-1.2 1.99-2.835 2.816-4.57l-3.611-1.72c-.76 1.598-1.526 2.863-2.225 3.668l3.02 2.622zm2.816-4.57c.46-.966.88-1.958 1.255-2.918l-3.726-1.456c-.348.891-.73 1.793-1.14 2.655l3.611 1.719zm.23-5.462L12.367 7.31l-1.676 3.632 5.559 2.566 1.676-3.632zM10.691 7.31L5.08 9.9l1.676 3.631 5.61-2.589-1.676-3.632z"
                                mask="url(#path-1-inside-1_968_11934)"
                              />
                            </svg>
                          </div>
                        }
                        filterLabel={label}
                        removeFilter={removeFilter}
                        filterType={SEARCH_FILTER_TYPES.GRADE_LEVELS}
                      />
                    );
                  })}
                  {activeSearchFilters?.channels?.map((id) => {
                    const label = searchFilters?.channels?.find(
                      (channel) => channel.id === id,
                    )?.name;
                    return (
                      <FilterPill
                        key={id}
                        id={id}
                        icon={
                          <div className="stroke-[#D4D4D8] w-6 h-6 fill-none stroke-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeMiterlimit="10"
                                d="M7 20s2.725-1 5.5-1 5.5 1 5.5 1M4.706 4h14.588C20.236 4 21 4.811 21 5.812v9.376c0 1-.764 1.812-1.706 1.812H4.706C3.764 17 3 16.189 3 15.188V5.812C3 4.812 3.764 4 4.706 4z"
                              />
                            </svg>
                          </div>
                        }
                        filterLabel={label}
                        removeFilter={removeFilter}
                        filterType={SEARCH_FILTER_TYPES.CHANNELS}
                      />
                    );
                  })}
                  {activeSearchFilters?.characters?.map((id) => {
                    const label = searchFilters?.characters?.find(
                      (character) => character.id === id,
                    )?.name;
                    return (
                      <FilterPill
                        key={id}
                        id={id}
                        icon={
                          <div className="stroke-[#D4D4D8] stroke-[3] fill-none w-6 h-6">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                            >
                              <circle cx="16.708" cy="9.704" r="4.566" />
                              <circle cx="6.888" cy="13.748" r="3.988" />
                              <path
                                strokeLinecap="round"
                                d="M14.373 17.963c1.333.964 4.8 2.12 8-.963M9.098 3.645c-.97-.036-3.226.434-4.49 2.607"
                              />
                            </svg>
                          </div>
                        }
                        filterLabel={label}
                        removeFilter={removeFilter}
                        filterType={SEARCH_FILTER_TYPES.CHARACTERS}
                      />
                    );
                  })}
                  {activeSearchFilters?.champions?.map((id) => {
                    const label = searchFilters?.characters?.find(
                      (champ) => champ.id === id,
                    )?.name;
                    return (
                      <FilterPill
                        key={id}
                        id={id}
                        icon={
                          <div className="stroke-[#D4D4D8] stroke-[3] fill-none w-6 h-6">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                            >
                              <circle cx="16.708" cy="9.704" r="4.566" />
                              <circle cx="6.888" cy="13.748" r="3.988" />
                              <path
                                strokeLinecap="round"
                                d="M14.373 17.963c1.333.964 4.8 2.12 8-.963M9.098 3.645c-.97-.036-3.226.434-4.49 2.607"
                              />
                            </svg>
                          </div>
                        }
                        filterLabel={label}
                        removeFilter={removeFilter}
                        filterType={SEARCH_FILTER_TYPES.CHAMPIONS}
                      />
                    );
                  })}
                </div>

                {showClearAllButton && (
                  <Pressable
                    onPress={() => {
                      setActiveSearchFilters({ clearFilters: true });
                      setActiveFilter({});
                    }}
                    className="order-3 min-w-fit ml-auto"
                  >
                    Clear all
                  </Pressable>
                )}
              </>
            )}
            <span
              className={twMerge(
                "order-4 min-w-fit lg:ml-auto pl-4 border-gray-500",
                numberOfResultsDisplayed
                  ? showClearAllButton
                    ? "border-l-2"
                    : "ml-auto"
                  : "",
              )}
            >
              {numberOfResultsDisplayed}
            </span>
          </div>
        </div>

        {searchByFiltersIsLoading ? (
          <Columns cols={4} loading bgColor="bg-transparent" />
        ) : searchByFiltersResults === undefined ? (
          <EmptyState />
        ) : searchByFiltersResults?.length === 0 ? (
          <NoResultsState />
        ) : (
          <div className="container pt-8 pb-lg flex flex-col gap-8 md:gap-10 lg:gap-12">
            <ul className="grid gap-x-md gap-y-md md:grid-cols-2 lg:grid-cols-4">
              {searchByFiltersPages.map((page, pageIndex) =>
                page.results.map((card, resultIndex) => (
                  <SearchResultCard
                    key={`${pageIndex}-${resultIndex}`}
                    card={card}
                    analyticsProps={{ sourceElement: analyticsSourceElement }}
                  />
                )),
              )}
            </ul>
            {searchByFiltersHasNextPage ? (
              <div className="mx-auto p-2">
                <Button
                  variant="vivid"
                  size="lg"
                  disabled={searchByFiltersIsFetchingNextPage}
                  onPress={() => searchByFiltersFetchNextPage()}
                >
                  Load more
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </Layout>
  );
};

export const getServerSideProps = withAuthorization(
  async (ctx) => {
    const fetcher = api(ctx);
    const queryClient = new QueryClient();

    await queryClient.fetchQuery(
      [QUERY_KEYS.SEARCH_FILTERS],
      fetcher.getSearchFilters,
    );

    return {
      props: {
        dehydratedState: dehydrate(queryClient),
      },
    };
  },

  ROUTE_PERMISSIONS.PUBLIC,
);

export default Search;
